"use client"

import Link from "@/components/common/Link"
import NextImage from "@/components/common/NextImage"
import Text from "@/components/common/Typography/Text"
import { TypeProduct } from "@/lib/redux/features/product/type"
import { TypeStoreDetail } from "@/lib/redux/features/store/type"
import { formatCurrencyUSD } from "@/utils/app/formatCurrencyUSD"
import { Alert, Button, Card, Col, ConfigProvider, Row, theme, Typography } from "antd"
import { useMemo, useRef, useState } from "react"
import PromoDuration from "@/components/layouts/BasicLayout/BasicLayoutPromoBar/PromoDuration"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFire } from "@fortawesome/free-solid-svg-icons"
import dayjs from 'dayjs'
import Title from "@/components/common/Typography/Title"

type ProductItemProps = {
  product: TypeProduct,
  customGridProduct?: number,
  store: TypeStoreDetail
}

const ProductItem = (props: ProductItemProps) => {
  const refInit = useRef<boolean>(false)
  const token = theme.useToken().token
  const { product, store } = props
  const gridProducts = store.grid_products
  const [shownPromo, setShownPromo] = useState<boolean>(false)
  const { defaultVariant, defaultColorAttribute } = useMemo(() => {
    const variant = product.variants?.find(v => v.id === product.default_variant) || (product?.variants || [])[0]
    const defaultColorAttribute = variant?.attributes?.find(attr => !!attr.color)
    return {
      defaultVariant: variant,
      defaultColorAttribute
    }
  }, [product.variants, product.default_variant])

  if (!refInit.current) {
    if (!product.discount_date_end) {
      setShownPromo(false)
    } else {
      const now = dayjs()
      const endDate = dayjs(product.discount_date_end)
      const duration = endDate.diff(now, 'second')
      setShownPromo(duration > 30)
    }
    refInit.current = true
  }

  const colors = useMemo(() => {
    return product.variants?.reduce((arr: any[], v) => {
      const colorAttr = v?.attributes?.find(attr => !!attr?.color)
      if (!arr.some(attr => (attr?.values?.color === colorAttr?.values?.color))) {
        arr.push(colorAttr)
      }
      return arr
    }, [defaultColorAttribute])
  }, [defaultColorAttribute, product.variants])

  const imageSize: any = {
    1: {
      height: 1200,
      width: 1200 * 0.8
    },
    2: {
      height: 1000,
      width: 1000 * 0.8
    },
    3: {
      height: 900,
      width: 900 * 0.8
    },
    4: {
      height: 700,
      width: 700 * 0.8
    },
    5: {
      height: 500,
      width: 500 * 0.8
    }
  }

  const imagesResponsive: any = {
    1: '(min-width: 74em) 75em, 100vw',
    2: '(min-width: 74em) 37em, 50vw',
    3: '(min-width: 74em) 25em, (min-width: 768px) 33vw, 50vw',
    4: '(min-width: 74em) 19em, (min-width: 992px) 25vw, (min-width: 768px) 33vw, 50vw',
    5: '(min-width: 74em) 15em, (min-width: 992px) 25vw, (min-width: 768px) 33vw, 50vw',
  }

  const price = useMemo(() => {
    if (!product.bundle) {
      return defaultVariant?.price || 0
    }
    return product?.bundle_data?.reduce((total, bundleD) => {
      return total + bundleD.variants[0].price
    }, 0)
  }, [product?.bundle_data, product.bundle, defaultVariant?.price])

  const comparePrice = useMemo(() => {
    if (!product.bundle) {
      return defaultVariant?.compare_at_price || 0
    }
    return product?.bundle_data?.reduce((total, bundleD) => {
      return total + (bundleD.variants[0].compare_at_price || 0)
    }, 0)
  }, [product?.bundle_data, product.bundle, defaultVariant?.price])

  const discountDateEnd = product.discount_date_end

  const elseColors = colors?.slice(3)?.length || 0
  const shownPrevieColors = store?.shown_preview_colors && product.attributes?.some(attr => !!attr.color)
  const secondaryCard = store.card_product === 'secondary'
  const shownTitle = !!product?.title
  let heightContent = !secondaryCard ? shownPrevieColors ? 15.5 : 12.5 : shownPrevieColors ? 10.8 : 7.2

  if (!shownTitle) {
    heightContent -= 3
  }

  const canShowPromo = !!comparePrice && !!discountDateEnd && !!shownPromo

  return (
    <ConfigProvider
      theme={{
        token: {
          colorText: store.product_card_text_color
        }
      }}
    >
      <Col
        style={{ paddingInline: '0.75rem' }}
        className={`product__list--item product__list--item-${gridProducts || 5}`}
      >
        <Link href={`/product/${props.product.slug}`}>
          <Card
            variant='borderless'
            hoverable
            style={{ maxWidth: "100%", height: "100%" }}
            styles={{
              cover: {
                alignItems: 'center',
                display: 'flex'
              },
              body: {
                justifyContent: 'space-between',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
            cover={(
              <NextImage
                {...imageSize[gridProducts || 5]}
                spin={{
                  wrapperClassName: 'product__list--item-img--spinner'
                }}
                className="product__list--item-img"
                sizes={imagesResponsive[gridProducts || 5]}
                style={{ width: "100%", height: "100%", objectFit: 'contain' }}
                draggable={false}
                fetchPriority='high'
                src={props.product.default_image || "/static/images/cat_square.png"}
                alt={`Product ${props.product.title}`}
              />
            )}
            size='small'
          >
            <div style={{ marginBottom: '1rem', ...(shownPrevieColors ? {} : { opacity: 0 }) }}>
              <Row align='middle'>
                {colors?.slice(0, 3).map((attrColor, idx) => {
                  const isWhite = attrColor?.color === 'White' || attrColor?.values?.color === '#ffffff'
                  return (
                    <Col
                      key={idx}
                      style={{
                        height: "2rem",
                        width: "2rem",
                        borderRadius: "50%",
                        background: (attrColor?.values?.texture === 'null' || !attrColor?.values?.texture)
                          ? isWhite ? '#eaeaea' : attrColor?.values?.color
                          : `url("${attrColor?.values?.texture}")`,
                        marginLeft: idx === 0 ? undefined : "-0.8rem"
                      }}
                    />
                  )
                })}
                {elseColors > 0 && (
                  <Col style={{ marginLeft: '0.5rem' }}>
                    <Text type='secondary' style={{ fontSize: '1.2rem' }}>
                      +{elseColors} color{elseColors > 1 ? 's' : ''}
                    </Text>
                  </Col>
                )}
              </Row>
            </div>
            <Card.Meta
              styles={{
                title: {
                  fontWeight: 'normal'
                }
              }}
              title={<Title level={3} style={{ fontWeight: 'normal', margin: 0, fontSize: '1.6rem' }}>{props.product.title}</Title>}
              description={(
                <>
                  <Row gutter={5}>
                    <Col>
                      <Typography.Text strong>
                        {formatCurrencyUSD.format(price || 0)}
                      </Typography.Text>
                    </Col>

                    {!!comparePrice && (
                      <Col>
                        <Typography.Text delete type='secondary' strong>
                          {formatCurrencyUSD.format(comparePrice || 0)}
                        </Typography.Text>
                      </Col>
                    )}
                  </Row>
                  {canShowPromo && (
                    <Row style={{ marginBlock: '0.5rem' }}>
                      <Col span={24}>
                        <Alert
                          // title="Sale ends in"
                          type='error'
                          // showIcon

                          style={{ padding: '0.5rem', border: 'none' }}
                          description={(
                            <>
                              <Row wrap={false} align='middle'>
                                <Col style={{ marginRight: '0.5rem' }}>
                                  <Text strong type='danger'>
                                    <FontAwesomeIcon icon={faFire} style={{ fontSize: '1.4rem' }} />
                                  </Text>
                                </Col>
                                <Col>
                                  <PromoDuration onEnded={() => setShownPromo(false)} textType='danger' type="ghost" discount={{ end_date: discountDateEnd }} />
                                </Col>
                              </Row>

                            </>
                          )}
                        />
                      </Col>
                    </Row>
                  )}
                </>
              )}
            />
            {!secondaryCard && (
              <div style={{ marginTop: '1rem' }}>
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: store.btn_view_product_bg_color
                    }
                  }}
                >
                  <Button
                    aria-label={`View detail product ${props.product.title}`}
                    htmlType="button"
                    size='large'
                    block
                    type='primary'
                    style={{
                      color: store.btn_view_product_text_color,
                      boxShadow: 'none',
                      height: "4rem"
                    }}
                  >
                    View Product
                  </Button>
                </ConfigProvider>
              </div>
            )}
          </Card>
        </Link>
      </Col>
    </ConfigProvider>
  )
}

export default ProductItem